import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { readonly, ref } from 'vue';
import { useWhiteLabelStore } from '@/stores/white-label/WhiteLabelStore';
import { useListingStore } from '@/stores/listing/ListingStore';
import { type Exhibitor, type ExhibitorFilter, getExhibitorService, type ResultList } from '@arcual/lib-app-platform';
import { defaultAccount } from '@/views/profile/DefaultAccount';

const exhibitorStoreName = 'exhibitorStore';
export const useExhibitorStore = defineStore(exhibitorStoreName, () => {
  const exhibitors: Record<string, Ref<Exhibitor>> = {};
  const exhibitorListingCounts = ref<Record<string, number>>({});

  const getExhibitor = async (id: string): Promise<Ref<Exhibitor | undefined>> => {
    const existing = exhibitors[id];

    if (existing) {
      return readonly(existing);
    }

    const activationId = useWhiteLabelStore().activationId;
    const result = await getExhibitorService()
      .getExhibitor(activationId, id)
      .catch(() => {
        // TODO: catch silently as operator might not be an exhibitor (remove once we solve for the operator)
        return undefined;
      });

    if (!result) {
      return ref(result);
    }

    if (!result.address) {
      result.address = defaultAccount().address;
    }

    exhibitors[id] = ref(result);
    return readonly(exhibitors[id]);
  };

  const getExhibitors = async (
    filters: ExhibitorFilter = {},
  ): Promise<ResultList<Exhibitor>> => {
    const {
      page = 1,
      search = '',
      includeListingCounts = true,
      pageSize,
    } = filters;

    const activationId = useWhiteLabelStore().activationId;
    const result = await getExhibitorService().getExhibitors(activationId, {
      page,
      search,
      pageSize,
      includeListingCounts,
    });

    for (const exhibitor of result.items) {
      exhibitors[exhibitor.id] = ref(exhibitor);
    }


    if (includeListingCounts) {
      const exhibitorIds = result.items.map((item: Exhibitor) => item.id);

      loadExhibitorListingCounts(exhibitorIds);
    }

    return { ...result };
  };

  const loadExhibitorListingCounts = (exhibitorIds: string[]) => {
    // setTimeout to ensure we don't block the UI
    setTimeout(async () => {
      try {
        // Load listing counts for each exhibitor in parallel
        await Promise.all(exhibitorIds.map(async (id) => {
          try {
            await getListingCount(id);
          } catch (err) {
            console.error(`Failed to load listing count for exhibitor ${id}:`, err);
          }
        }));
      } catch (error) {
        console.error('Error loading exhibitor listing counts:', error);
      }
    }, 0);
  };


  const getListingCount = async (exhibitorId: string): Promise<number> => {
    try {
      const listings = await useListingStore().postListings(
        { filterOnSellerId: [exhibitorId], page: 1 },
        false,
      );

      const count = listings.total;

      exhibitorListingCounts.value = {
        ...exhibitorListingCounts.value,
        [exhibitorId]: count,
      };

      return count;
    } catch (error) {
      console.error(`Error fetching listing count for exhibitor ${exhibitorId}:`, error);
      return 0;
    }
  };

  const getExhibitorListingCount = (exhibitorId: string): number | undefined => {
    return exhibitorListingCounts.value[exhibitorId];
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    // Clear the exhibitors cache
    for (const key in exhibitors) {
      delete exhibitors[key];
    }

    // Clear listing counts
    exhibitorListingCounts.value = {};
  };

  return {
    getExhibitor,
    getExhibitors,
    getListingCount,
    getExhibitorListingCount,
    reset,
  };
});
